import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { BorderedRichTextEditor } from '@/messages/BorderedRichTextEditor';
import { useRichTextEditor } from '~/hooks/useRichTextEditor';
import { FormContainer, GroupHeaderWrapper } from '~/styles/layoutElements';
import { MASS_MESSAGING_SENT_FLAG } from './$groupName.mass-messaging.index';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/mass-messaging/new',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showEmptyMessageError, setShowEmptyMessageError] = useState(false);
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/mass-messaging/new',
  });

  const editor = useRichTextEditor();
  const { editorRef, getEditorContent } = editor;

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount to set initial editor content
  useEffect(() => {
    // .ORG uses a URL param like "?m_ref=31" to pass the message content when clicking "repost". Consider implementing something like this.
    const repostedMessage = sessionStorage.getItem('repostedMessage');
    if (repostedMessage && editorRef.current) {
      editorRef.current.innerHTML = repostedMessage;
      sessionStorage.removeItem('repostedMessage');
    }
  }, []);

  const handleBack = () => {
    navigate({
      to: '/groups/$groupName/mass-messaging/',
      params: { groupName },
    });
  };

  const handleSend = () => {
    if (!getEditorContent()) {
      setShowEmptyMessageError(true);
      return;
    }

    setShowEmptyMessageError(false);
    // TODO: submit to API
    sessionStorage.setItem(MASS_MESSAGING_SENT_FLAG, '1');
    navigate({
      to: '/groups/$groupName/mass-messaging/',
      params: { groupName },
    });
  };

  return (
    <>
      {showEmptyMessageError && (
        <div style={{ marginTop: '16px' }}>
          <AlertBox variant="error">
            {t('wbs.please-enter-message-content')}
          </AlertBox>
        </div>
      )}
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('wbs.compose-a-new-message')} />
      </GroupHeaderWrapper>
      <FormContainer>
        <LargeArrowBackButton onClick={handleBack} />
        <Grid>
          <SectionLabel>{t('wbs.recipients')}</SectionLabel>
          {/* Translation Needed */}
          <RecipientsText>
            Message will be sent to all the Study Helpers.
          </RecipientsText>

          <SectionLabel>{t('wbs.message')}</SectionLabel>
          <EditorSection>
            <BorderedRichTextEditor
              editor={editor}
              visibleTools={['bold', 'italic', 'underline', 'blockquote']}
              maxHeight="400px"
              placeholder=""
              onSubmit={handleSend}
            />
            <SendRow>
              <WBSButton wide type="button" onClick={handleSend}>
                {t('wbs.send')}
              </WBSButton>
            </SendRow>
          </EditorSection>
        </Grid>
      </FormContainer>
    </>
  );
}

const Grid = styled.div`
  align-items: start;
  display: grid;
  gap: 28px 30px;
  grid-template-columns: auto 1fr;
`;

const SectionLabel = styled.span`
  color: var(--black);
  font-size: 13px;
  font-weight: 600;
  padding-top: 6px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const RecipientsText = styled.p`
  color: var(--black);
  font-size: 13px;
  margin: 0;
  padding-top: 4px;
`;

const EditorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SendRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;
